import React, { useEffect, useMemo, useState } from "react";
import {
  useAddDevicesToGroupMutation,
  useGetGroupDevicesQuery,
} from "../../redux/admin/groupApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { toast } from "react-hot-toast";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import { X, Search, Smartphone, Plus } from "lucide-react";

const AddDeviceToGroupModal = ({ groupId, isOpen, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedDeviceIds([]);
      setSearchQuery("");
    }
  }, [isOpen]);

  const { data: allDevices, isLoading: isDevicesLoading } =
    useGetAllLiteDevicesQuery(undefined, { skip: !isOpen });

  const { data: currentGroupDevices, isFetching: isGroupDevicesFetching } =
    useGetGroupDevicesQuery({ groupId }, { skip: !isOpen || !groupId });

  const [addDevicesToGroup, { isLoading: isAdding }] =
    useAddDevicesToGroupMutation();

  const devices = useMemo(() => {
    const deviceList =
      allDevices?.data?.devices ?? allDevices?.data ?? allDevices ?? [];
    return Array.isArray(deviceList) ? deviceList : [];
  }, [allDevices]);

  const devicesInGroup = useMemo(() => {
    const groupDevices =
      currentGroupDevices?.data?.devices ?? currentGroupDevices?.devices ?? [];
    if (!Array.isArray(groupDevices)) return new Set();
    return new Set(groupDevices.map((d) => d._id));
  }, [currentGroupDevices]);

  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devices;
    const q = searchQuery.toLowerCase();
    return devices.filter(
      (d) =>
        String(d.name || "").toLowerCase().includes(q) ||
        String(d.imei || "").includes(q) ||
        String(d.serialNumber || "").toLowerCase().includes(q)
    );
  }, [devices, searchQuery]);

  const isInGroup = (deviceId) => devicesInGroup.has(deviceId);

  const availableDevices = useMemo(
    () => filteredDevices.filter((d) => !isInGroup(d._id)),
    [filteredDevices, devicesInGroup]
  );

  const availableCount = availableDevices.length;

  const handleToggleDevice = (deviceId) => {
    if (isInGroup(deviceId)) return;
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    setSelectedDeviceIds(availableDevices.map((d) => d._id));
  };

  const handleClearSelection = () => {
    setSelectedDeviceIds([]);
  };

  const getDeviceStatus = (device) => {
    const rawStatus =
      device?.status ||
      device?.liveData?.status ||
      device?.currentStatus ||
      (device?.isOnline ? "online" : "offline");
    const status = String(rawStatus || "offline").toLowerCase();
    if (status.includes("idle") || status.includes("stop")) return "idle";
    if (status.includes("online") || status.includes("running")) return "online";
    return "offline";
  };

  const getStatusMeta = (status) => {
    const statusMap = {
      online: { label: "Online", color: "#16a34a", bg: alpha("#16a34a", 0.1) },
      idle: { label: "Idle", color: "#f59e0b", bg: alpha("#f59e0b", 0.12) },
      offline: { label: "Offline", color: "#dc2626", bg: alpha("#dc2626", 0.09) },
    };
    return statusMap[status] || statusMap.offline;
  };

  const submitLabel =
    selectedDeviceIds.length === 0
      ? "Add Selected Devices"
      : `Add ${selectedDeviceIds.length} Device${
          selectedDeviceIds.length > 1 ? "s" : ""
        }`;

  const handleSubmit = async () => {
    if (!groupId) {
      toast.error("Group not found");
      return;
    }
    if (selectedDeviceIds.length === 0) {
      toast.error("Please select at least one device");
      return;
    }

    try {
      await addDevicesToGroup({
        groupId,
        deviceIds: selectedDeviceIds,
      }).unwrap();

      toast.success(
        `${selectedDeviceIds.length} device${
          selectedDeviceIds.length > 1 ? "s" : ""
        } added successfully!`
      );
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.msg || "Failed to add devices");
    }
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "90vw", md: 900 },
          maxWidth: 940,
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.28)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.2 },
          background: "#fff",
          borderBottom: `1px solid ${alpha("#94a3b8", 0.22)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Stack direction="row" spacing={1.8} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: { xs: 50, sm: 58 },
                height: { xs: 50, sm: 58 },
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
                color: "#2563eb",
                flexShrink: 0,
              }}
            >
              <Smartphone size={26} />
            </Box>
            <Box minWidth={0}>
              <Typography
                variant="h6"
                sx={{
                  color: "#0f172a",
                  fontWeight: 950,
                  fontSize: { xs: 20, sm: 23 },
                  lineHeight: 1.15,
                }}
              >
                Add Devices to Group
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.35, color: "#475569", fontWeight: 600 }}
              >
                Select devices you want to add to this group.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {(isGroupDevicesFetching || isDevicesLoading) && (
              <Chip
                size="small"
                label="Loading..."
                variant="outlined"
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            )}
            <Tooltip title="Close">
              <IconButton onClick={onClose} sx={{ borderRadius: 2, color: "#64748b" }}>
                <X size={20} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: "#fff" }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${alpha("#94a3b8", 0.18)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.4}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by device name or IMEI..."
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={19} color="#64748b" />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 46,
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  fontWeight: 600,
                  "& fieldset": { borderColor: alpha("#94a3b8", 0.38) },
                  "&:hover fieldset": { borderColor: alpha("#2563eb", 0.45) },
                  "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                },
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              sx={{ flexShrink: 0 }}
            >
              <Chip
                label={`${availableCount} Available`}
                sx={{
                  height: 34,
                  borderRadius: 999,
                  color: "#2563eb",
                  bgcolor: alpha("#2563eb", 0.09),
                  fontWeight: 900,
                  px: 1,
                }}
              />
              <Chip
                label={`${selectedDeviceIds.length} Selected`}
                sx={{
                  height: 34,
                  borderRadius: 999,
                  color: "#6d28d9",
                  bgcolor: alpha("#7c3aed", 0.11),
                  fontWeight: 900,
                  px: 1,
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <TableContainer
          sx={{
            maxHeight: fullScreen ? "calc(100vh - 238px)" : 520,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Table
            stickyHeader
            sx={{
              tableLayout: "fixed",
              minWidth: 0,
              "& .MuiTableCell-root": {
                borderBottom: `1px solid ${alpha("#94a3b8", 0.18)}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: { xs: 46, sm: 58 }, bgcolor: "#f8fafc" }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedDeviceIds.length > 0 &&
                      selectedDeviceIds.length < availableDevices.length
                    }
                    checked={
                      availableDevices.length > 0 &&
                      selectedDeviceIds.length === availableDevices.length
                    }
                    disabled={availableDevices.length === 0 || isDevicesLoading}
                    onChange={(e) =>
                      e.target.checked ? handleSelectAll() : handleClearSelection()
                    }
                    sx={{
                      p: 0.4,
                      color: "#64748b",
                      "&.Mui-checked": { color: "#2563eb" },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: "37%",
                    bgcolor: "#f8fafc",
                    color: "#334155",
                    fontWeight: 900,
                    fontSize: 13,
                  }}
                >
                  Vehicle Name
                </TableCell>
                <TableCell
                  sx={{
                    width: "34%",
                    bgcolor: "#f8fafc",
                    color: "#334155",
                    fontWeight: 900,
                    fontSize: 13,
                  }}
                >
                  IMEI
                </TableCell>
                <TableCell
                  sx={{
                    width: "23%",
                    bgcolor: "#f8fafc",
                    color: "#334155",
                    fontWeight: 900,
                    fontSize: 13,
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isDevicesLoading ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <TableRow key={`loading-${i}`} sx={{ height: 62 }}>
                    <TableCell>
                      <Skeleton variant="rounded" width={20} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="70%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="78%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={72} height={26} />
                    </TableCell>
                  </TableRow>
                ))
              ) : availableDevices.length > 0 ? (
                availableDevices.map((device) => {
                  const isSelected = selectedDeviceIds.includes(device._id);
                  const status = getDeviceStatus(device);
                  const statusMeta = getStatusMeta(status);

                  return (
                    <TableRow
                      key={device._id}
                      hover
                      onClick={() => handleToggleDevice(device._id)}
                      sx={{
                        height: 62,
                        cursor: "pointer",
                        bgcolor: isSelected ? alpha("#2563eb", 0.07) : "#fff",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          bgcolor: isSelected
                            ? alpha("#2563eb", 0.1)
                            : alpha("#2563eb", 0.035),
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleDevice(device._id)}
                          sx={{
                            p: 0.4,
                            color: "#64748b",
                            "&.Mui-checked": { color: "#2563eb" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          title={device.name || "Unnamed Device"}
                          sx={{
                            color: "#0f172a",
                            fontWeight: 900,
                            fontSize: { xs: 13, sm: 14 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {device.name || "Unnamed Device"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          sx={{
                            color: "#475569",
                            fontWeight: 600,
                            fontSize: { xs: 12.5, sm: 14 },
                            overflowWrap: "anywhere",
                          }}
                        >
                          {device.imei || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.7,
                            px: 1.2,
                            py: 0.45,
                            borderRadius: 999,
                            bgcolor: statusMeta.bg,
                            color: statusMeta.color,
                            fontSize: 12,
                            fontWeight: 900,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: statusMeta.color,
                              flexShrink: 0,
                            }}
                          />
                          {statusMeta.label}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ borderBottom: 0 }}>
                    <Box
                      sx={{
                        minHeight: 220,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 58,
                          height: 58,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha("#2563eb", 0.08),
                          color: "#2563eb",
                        }}
                      >
                        <Smartphone size={26} />
                      </Box>
                      <Typography sx={{ color: "#0f172a", fontWeight: 950 }}>
                        No devices found
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 600 }}>
                        {searchQuery
                          ? "Try a different device name or IMEI."
                          : "No available devices can be added to this group."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: `1px solid ${alpha("#94a3b8", 0.18)}`,
          background: "#fff",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: "100%" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isAdding}
            sx={{
              minWidth: 96,
              height: 42,
              borderRadius: "8px",
              color: "#334155",
              borderColor: alpha("#64748b", 0.28),
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isAdding || selectedDeviceIds.length === 0}
            startIcon={
              isAdding ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                <Plus size={18} />
              )
            }
            sx={{
              minWidth: { xs: "100%", sm: 218 },
              height: 42,
              borderRadius: "8px",
              fontWeight: 900,
              textTransform: "none",
              background: "linear-gradient(135deg, #818cf8 0%, #7c3aed 100%)",
              boxShadow: "0 12px 26px rgba(124, 58, 237, 0.24)",
              "&:hover": {
                background: "linear-gradient(135deg, #6366f1 0%, #6d28d9 100%)",
              },
              "&.Mui-disabled": {
                color: "#fff",
                background: alpha("#7c3aed", 0.5),
              },
            }}
          >
            {isAdding ? "Adding..." : submitLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddDeviceToGroupModal;
